import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useModalEscape from "../../hooks/useModalEscape";
import useModalTransition from "../../hooks/useModalTransition";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import {
  Sparkles,
  FileCheck,
  Globe,
  CheckCircle,
  Building2,
  X,
  Info,
  Shield,
  Rocket,
  Loader2,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Check,
} from "lucide-react";
import { db } from "../../firebase";
import { API_BASE_URL } from "../../config";
import { toast } from "react-toastify";
import LguRequestModal from "../../components/LguRequestModal/LguRequestModal";
import "./PublicLandingPage.css";
import TermsOfServiceModal from "../../components/LegalModals/TermsOfServiceModal";
import PrivacyPolicyModal from "../../components/LegalModals/PrivacyPolicyModal";
import examplePhone from "../../example.png";
import conversationPhone from "../../conversation.png";
import img1 from "../../screenshots/1.png";
import img2 from "../../screenshots/2.png";
import img3 from "../../screenshots/3.png";
import img4 from "../../screenshots/4.png";
import img5 from "../../screenshots/5.png";
import img6 from "../../screenshots/6.png";

const SCREENSHOTS = [img1, img2, img3, img4, img5, img6];

// ── MODAL COMPONENTS (Copied from Settings.jsx) ────────────────

const DownloadModal = ({ isOpen, onClose }) => {
  useModalEscape(onClose, isOpen);
  const shouldRender = useModalTransition(isOpen);
  if (!shouldRender) return null;
  return (
    <div
      className={`add-user-modal-overlay modal-transition-${isOpen ? "enter" : "exit"}`}
      onClick={onClose}
    >
      <div
        className="add-user-modal-content download-modal-custom"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="add-user-modal-close" onClick={onClose}>
          ✕
        </button>

        <div className="add-user-modal-body" style={{ textAlign: "center" }}>
          <h2
            className="plp-modal-title"
            style={{
              fontSize: "24px",
              fontWeight: 700,
              margin: "0 0 4px 0",
              color: "#1c1d1e",
            }}
          >
            Download Avisa
          </h2>
          <p style={{ fontSize: "15px", color: "#64748b", margin: "0" }}>
            Get Avisa on your mobile device
          </p>
          <p
            style={{
              fontSize: "13px",
              color: "#94a3b8",
              margin: "0 0 12px 0",
              fontWeight: 500,
            }}
          >
            Current version: {process.env.REACT_APP_VERSION || "v1.0.0-rc.3"}
          </p>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              width: "100%",
            }}
          >
            {/* Try Web Version Button (Primary) */}
            <a
              href="https://mobile.avisa.site"
              target="_blank"
              rel="noopener noreferrer"
              className="plp-btn plp-btn-primary"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                padding: "14px 20px",
                borderRadius: "12px",
                textDecoration: "none",
                fontWeight: 600,
                fontSize: "15px",
                width: "100%",
                boxSizing: "border-box",
                cursor: "pointer",
              }}
            >
              <span>Try Web Version</span>
            </a>

            {/* Download on Android Button (Secondary) */}
            <a
              href={process.env.REACT_APP_APK_DOWNLOAD_URL || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="plp-btn plp-btn-outline"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                padding: "14px 20px",
                borderRadius: "12px",
                textDecoration: "none",
                fontWeight: 600,
                fontSize: "15px",
                width: "100%",
                boxSizing: "border-box",
                cursor: "pointer",
              }}
            >
              <span>Download on Android</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const ScrollToTopButton = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShow(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          style={{
            position: "fixed",
            bottom: "32px",
            right: "32px",
            padding: "12px 20px",
            borderRadius: "24px",
            background: "#0f172a",
            color: "white",
            border: "none",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            zIndex: 100,
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          <ChevronUp size={16} />
          Go to Top
        </motion.button>
      )}
    </AnimatePresence>
  );
};

const AboutModal = ({ isOpen, onClose }) => {
  useModalEscape(onClose, isOpen);
  const shouldRender = useModalTransition(isOpen);
  if (!shouldRender) return null;
  return (
    <div
      className={`plp-modal-overlay modal-transition-${isOpen ? "enter" : "exit"}`}
      onClick={onClose}
    >
      <div className="about-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="about-modal-close" onClick={onClose}>
          <X size={20} />
        </button>

        <div className="privacy-header">
          <h2
            className="about-title plp-modal-title"
            style={{ margin: 0, color: "#1c1d1e" }}
          >
            About Avisa
          </h2>
          <p className="about-subtitle">Our mission and team</p>
        </div>

        <div className="about-body">
          <div className="about-section">
            <p>
              Avisa is an AI-Enhanced Legal Information Management System
              designed to bridge the legal literacy gap in the Philippines by
              making national laws and local ordinances accessible to every
              Filipino citizen. Developed as a capstone project by students from
              National University, the platform transforms complex legal
              documents into plain-language summaries and Filipino translations
              using Google's Gemini AI, while preserving legal accuracy through
              expert verification. The system features a jurisdiction-aware
              search that prioritizes relevant barangay and municipal
              ordinances, a RAG-powered chatbot that answers legal questions
              with cited sources, and direct access to original scanned
              documents for verification. Built with a three-tier cloud-native
              architecture using React Native, React.js, and Firebase, Avisa
              serves both citizens seeking to understand their rights and Local
              Government Units needing efficient ordinance management tools. The
              platform aligns with UN Sustainable Development Goal 16 by
              promoting equal access to justice and strengthening transparency
              between the public and local institutions.
            </p>
          </div>

          <div className="about-footer">
            <p>© 2026 Avisa. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// TermsOfServiceModal and PrivacyPolicyModal are imported from components/LegalModals

const LGURequestModal = (props) => <LguRequestModal {...props} mode="public" />;

const ScopeLimitationsModal = ({ isOpen, onClose }) => {
  useModalEscape(onClose, isOpen);
  const shouldRender = useModalTransition(isOpen);
  if (!shouldRender) return null;

  return (
    <div
      className={`plp-modal-overlay modal-transition-${isOpen ? "enter" : "exit"}`}
      onClick={onClose}
    >
      <div
        className="privacy-modal-content scope-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="privacy-modal-close" onClick={onClose}>
          <X size={20} />
        </button>

        <div className="privacy-header">
          <h2 className="privacy-title plp-modal-title" style={{ margin: 0 }}>
            Scope &amp; Limitations
          </h2>
          <p className="privacy-subtitle">
            Project coverage and defined boundaries
          </p>
        </div>

        <div className="privacy-body scope-modal-body">
          <section className="scope-section">
            <h3>Scope of the Project</h3>
            <p>
              This study focuses on the design, development, and technical
              validation of Avisa, an AI-integrated legal information system. It
              consists of a cross-platform mobile application for citizens and a
              web-based administrative control center for local officials, built
              on a Three-Tier Cloud-Native Architecture.
            </p>
            <p>
              Avisa uses Gemini 3.1 Flash Lite to transform unstructured legal
              documents into structured, bilingual English and Filipino data.
              The model was selected for its low-cost API rates, native
              multimodal capabilities, and fast output generation. The pilot
              covers national mother laws connected to local ordinances within
              one selected LGU and three pilot barangays for hyper-local
              ordinance mapping.
            </p>
            <p>
              The system includes a Context-Augmented Generation chat interface,
              role-based access control, real-time monitoring of national laws,
              and expert validation of AI-generated summaries. Barangay document
              representatives receive scoped view, edit, and upload privileges
              supported by audit logging, two-step validation, and
              appointment-based activation.
            </p>
            <p>
              The project also includes LGU onboarding through landing-page
              submissions and administrator-approved municipal account
              provisioning for multi-tenant scalability. Its credit-based model
              provides five free daily credits, reset at midnight PHT; text
              queries use one credit and image queries use three credits, with
              PayMongo and webhook-based balance updates supporting paid usage.
            </p>
          </section>

          <section className="scope-section">
            <h3>Delimitations and Limitations</h3>
            <p>
              Manual data gathering and encoding are limited to one selected
              LGU, three selected pilot barangays within that municipality, and
              the mother laws connected to their local ordinances. The system
              supports future expansion through its multi-tenant architecture,
              but the following are outside this study:
            </p>
            <ul>
              <li>
                Manual gathering, scanning, and verification for non-pilot
                barangays or complete municipality-wide ordinance collections.
              </li>
              <li>
                Municipality-wide automated ordinance ingestion and
                verification.
              </li>
              <li>
                Official legal advice, legal filings, or replacement of formal
                legal processes and official acts of local legislatures.
              </li>
              <li>
                Offline AI operation, because Gemini API and Firebase Cloud
                services require internet connectivity.
              </li>
              <li>
                Public access to the administrative web application, which is
                restricted to authorized administrative users.
              </li>
            </ul>
            <p>
              Avisa is strictly an informational tool. AI-generated
              interpretations and plain-language summaries do not constitute
              legal advice. Legal experts will review outputs for accuracy,
              completeness, and safety, and their findings will guide further
              refinement before wider rollout.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

const PublicLandingPage = () => {
  useDocumentTitle("Avisa");

  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [isLGUOpen, setIsLGUOpen] = useState(false);
  const [isScopeOpen, setIsScopeOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="plp-root" id="public-landing-page">
      {/* ── HEADER ────────────────────────────────── */}
      <motion.header
        className={`plp-header ${isMobileMenuOpen ? "plp-header-mobile-open" : ""}`}
        id="plp-header"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="plp-header-inner">
          <img
            src="/images/avisa.svg"
            alt="Avisa"
            className="plp-logo"
            id="plp-logo"
          />
          <nav className="plp-nav" aria-label="Main navigation">
            <button
              onClick={() => setIsAboutOpen(true)}
              className="plp-nav-link"
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              About
            </button>
            <button
              onClick={() => setIsScopeOpen(true)}
              className="plp-nav-link"
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              Scope &amp; Limitations
            </button>
            <button
              onClick={() => setIsTermsOpen(true)}
              className="plp-nav-link"
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              Terms of Service
            </button>
            <button
              onClick={() => setIsPrivacyOpen(true)}
              className="plp-nav-link"
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              Privacy Policy
            </button>
            <button
              onClick={() => setIsDownloadOpen(true)}
              className="plp-btn plp-btn-primary"
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                marginLeft: "16px",
              }}
            >
              Download Avisa
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="plp-mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            <span className={`plp-hamburger ${isMobileMenuOpen ? "open" : ""}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>

          {/* Mobile Navigation Overlay */}
          <div className={`plp-mobile-nav ${isMobileMenuOpen ? "open" : ""}`}>
            <button
              onClick={() => {
                setIsAboutOpen(true);
                setIsMobileMenuOpen(false);
              }}
              className="plp-mobile-nav-link"
            >
              About
            </button>
            <button
              onClick={() => {
                setIsScopeOpen(true);
                setIsMobileMenuOpen(false);
              }}
              className="plp-mobile-nav-link"
            >
              Scope &amp; Limitations
            </button>
            <button
              onClick={() => {
                setIsTermsOpen(true);
                setIsMobileMenuOpen(false);
              }}
              className="plp-mobile-nav-link"
            >
              Terms of Service
            </button>
            <button
              onClick={() => {
                setIsPrivacyOpen(true);
                setIsMobileMenuOpen(false);
              }}
              className="plp-mobile-nav-link"
            >
              Privacy Policy
            </button>
            <div style={{ padding: "16px", marginTop: "8px" }}>
              <button
                onClick={() => {
                  setIsDownloadOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                className="plp-btn plp-btn-primary"
                style={{
                  width: "100%",
                  justifyContent: "center",
                  padding: "12px",
                }}
              >
                Download Avisa
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* ── HERO ──────────────────────────────────── */}
      <section className="plp-hero" id="plp-hero" aria-label="Hero section">
        <div className="plp-hero-content">
          <motion.h1
            className="plp-headline"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Justice Starts with
            <span className="plp-headline-accent"> Understanding</span>
          </motion.h1>

          <motion.p
            className="plp-sub"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Navigate Philippine law with intelligent summaries, bilingual
            support, and jurisdiction-aware search — built for citizens and
            Local Government Units.
          </motion.p>

          <motion.div
            className="plp-cta-group"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <button
              type="button"
              className="plp-btn plp-btn-primary"
              id="btn-download-avisa"
              style={{ height: "46px" }}
              onClick={() => setIsDownloadOpen(true)}
            >
              Download Avisa
            </button>
            <button
              type="button"
              className="plp-btn plp-btn-outline"
              id="btn-lgu-access"
              onClick={() => setIsLGUOpen(true)}
            >
              Request LGU Access
            </button>
          </motion.div>
        </div>

        <motion.div
          className="plp-hero-visual"
          aria-hidden="true"
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <div className="plp-phone-stack">
            <div className="plp-phone-frame plp-phone-frame--back">
              <img
                src={conversationPhone}
                alt="AVISA conversation screen"
                className="plp-phone-img"
              />
            </div>
            <div className="plp-phone-frame plp-phone-frame--front">
              <img
                src={examplePhone}
                alt="AVISA mobile app screenshot"
                className="plp-phone-img"
              />
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── FEATURE CARDS ───────────────────────────── */}
      <section
        style={{
          padding: "0 20px 140px 20px",
          width: "100%",
          boxSizing: "border-box",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "60px",
            justifyContent: "center",
            flexWrap: "wrap",
            maxWidth: "1100px",
            margin: "0 auto",
          }}
        >
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
            style={{
              display: "flex",
              flexDirection: "column",
              width: "320px",
              flex: "0 0 auto",
              boxSizing: "border-box",
            }}
          >
            <h3
              style={{
                fontSize: "22px",
                fontWeight: "800",
                color: "#1c1d1e",
                margin: "0 0 16px 0",
              }}
            >
              Smart AI
            </h3>
            <p
              style={{
                fontSize: "15px",
                color: "#6b7280",
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              Powered by advanced AI to quickly break down and explain complex
              Philippine laws into simple, plain-language summaries.
            </p>
          </motion.div>

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
            style={{
              display: "flex",
              flexDirection: "column",
              width: "320px",
              flex: "0 0 auto",
              boxSizing: "border-box",
            }}
          >
            <h3
              style={{
                fontSize: "22px",
                fontWeight: "800",
                color: "#1c1d1e",
                margin: "0 0 16px 0",
              }}
            >
              Cited Sources
            </h3>
            <p
              style={{
                fontSize: "15px",
                color: "#6b7280",
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              Every interpretation is backed by exact legal citations, so you
              always know exactly which laws and provisions are being
              referenced.
            </p>
          </motion.div>

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.3 }}
            style={{
              display: "flex",
              flexDirection: "column",
              width: "320px",
              flex: "0 0 auto",
              boxSizing: "border-box",
            }}
          >
            <h3
              style={{
                fontSize: "22px",
                fontWeight: "800",
                color: "#1c1d1e",
                margin: "0 0 16px 0",
              }}
            >
              Local to National
            </h3>
            <p
              style={{
                fontSize: "15px",
                color: "#6b7280",
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              Comprehensive coverage from national Republic Acts down to
              provincial and municipal ordinances for your specific locality.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── CAROUSEL ──────────────────────────────── */}
      <section
        className="plp-carousel-section"
        aria-label="App preview screenshots"
      >
        <motion.div
          className="plp-carousel-wrapper"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <div className="plp-carousel-track">
            {[...SCREENSHOTS, ...SCREENSHOTS].map((src, i) => (
              <img
                className="plp-carousel-img"
                key={i}
                src={src}
                alt={`App Screenshot ${i + 1}`}
                loading="lazy"
              />
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── FOOTER ────────────────────────────────── */}
      <footer className="plp-footer">
        <div className="plp-footer-inner">
          <div className="plp-footer-main">
            <div className="plp-footer-brand">
              <img
                src="/images/avisa.svg"
                alt="Avisa"
                className="plp-footer-logo"
              />
              <p>
                Making Philippine legal information easier to find, understand,
                and use for every Filipino.
              </p>
            </div>

            <div className="plp-footer-links" aria-label="Footer navigation">
              <div className="plp-footer-column">
                <h3>Explore</h3>
                <button type="button" onClick={() => setIsAboutOpen(true)}>
                  About Avisa
                </button>
                <button type="button" onClick={() => setIsScopeOpen(true)}>
                  Scope &amp; Limitations
                </button>
              </div>

              <div className="plp-footer-column">
                <h3>Get Avisa</h3>
                <button type="button" onClick={() => setIsDownloadOpen(true)}>
                  Download the app
                </button>
                <button type="button" onClick={() => setIsLGUOpen(true)}>
                  For Local Government Units
                </button>
              </div>

              <div className="plp-footer-column">
                <h3>Legal</h3>
                <button type="button" onClick={() => setIsTermsOpen(true)}>
                  Terms of Service
                </button>
                <button type="button" onClick={() => setIsPrivacyOpen(true)}>
                  Privacy Policy
                </button>
              </div>
            </div>
          </div>

          <div className="plp-footer-bottom">
            <p className="plp-footer-copy">
              &copy; {new Date().getFullYear()} Avisa. All rights reserved.
            </p>
            <p className="plp-footer-note">Justice Starts with Understanding</p>
          </div>
        </div>
      </footer>

      {/* ── MODALS ────────────────────────────────── */}
      <DownloadModal
        isOpen={isDownloadOpen}
        onClose={() => setIsDownloadOpen(false)}
      />
      <AboutModal isOpen={isAboutOpen} onClose={() => setIsAboutOpen(false)} />
      <TermsOfServiceModal
        isOpen={isTermsOpen}
        onClose={() => setIsTermsOpen(false)}
      />
      <PrivacyPolicyModal
        isOpen={isPrivacyOpen}
        onClose={() => setIsPrivacyOpen(false)}
      />
      <LGURequestModal isOpen={isLGUOpen} onClose={() => setIsLGUOpen(false)} />
      <ScopeLimitationsModal
        isOpen={isScopeOpen}
        onClose={() => setIsScopeOpen(false)}
      />
    </div>
  );
};

export default PublicLandingPage;
